import { useState, useEffect } from "react";
import { ChevronDown, Trash2, Plus } from "lucide-react";
import { toast } from "react-toastify";
import { Button } from "@/components/ui/button-tw";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/utils/classnames";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  useNotificationInfo,
  useAddNotification,
  useDeleteNotification,
} from "@/queries/notifications";

interface NotificationsSectionProps {
  userId: string;
  isAdmin: boolean;
}

export const NotificationsSection = ({
  userId,
  isAdmin,
}: NotificationsSectionProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [emailRecipients, setEmailRecipients] = useState<string[]>([]);
  const [currentEmail, setCurrentEmail] = useState("");

  const { data: notificationInfo } = useNotificationInfo({
    userId,
    subject: "invoice",
  });

  const addNotificationMutation = useAddNotification({
    onSuccess: () => {
      toast.success("Email recipient added successfully!");
    },
    onError: (error) => {
      toast.error("Failed to add email recipient. Please try again.");
      console.error("Error adding notification:", error);
    },
  });

  const deleteNotificationMutation = useDeleteNotification({
    onSuccess: () => {
      toast.success("Email recipient removed successfully!");
    },
    onError: (error) => {
      toast.error("Failed to remove email recipient. Please try again.");
      console.error("Error deleting notification:", error);
    },
  });

  useEffect(() => {
    if (notificationInfo && notificationInfo.length > 0) {
      const emails: string[] = [];

      notificationInfo.forEach((item) => {
        item.notification_info.forEach((notification) => {
          if (
            notification.notification_type === "email" &&
            notification.active &&
            notification.notification_value
          ) {
            emails.push(notification.notification_value);
          }
        });
      });

      setEmailRecipients(emails);
    }
  }, [notificationInfo]);

  const isValidEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleAddEmail = () => {
    const trimmedEmail = currentEmail.trim();

    if (!trimmedEmail) return;

    if (!isValidEmail(trimmedEmail)) {
      toast.error("Please enter a valid email address");
      return;
    }

    if (emailRecipients.includes(trimmedEmail)) {
      toast.error("This email is already in the list");
      return;
    }

    if (!userId) return;

    addNotificationMutation.mutate(
      {
        userId,
        notificationSubject: "invoice",
        payload: {
          notification_type: "email",
          notification_value: trimmedEmail,
          process_flow_step: "invoice_payment",
          active: true,
        },
      },
      {
        onSuccess: () => {
          setEmailRecipients((prev) => [...prev, trimmedEmail]);
          setCurrentEmail("");
        },
      },
    );
  };

  const handleRemoveEmail = (emailToRemove: string) => {
    if (!userId) return;

    deleteNotificationMutation.mutate(
      {
        userId,
        notificationSubject: "invoice",
        payload: {
          notification_type: "email",
          notification_value: emailToRemove,
          process_flow_step: "invoice_payment",
          active: true,
        },
      },
      {
        onSuccess: () => {
          setEmailRecipients((prev) =>
            prev.filter((email) => email !== emailToRemove),
          );
        },
      },
    );
  };

  const handleEmailKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleAddEmail();
    }
  };

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <div className="bg-white rounded-lg shadow">
        <CollapsibleTrigger className="w-full p-6 flex items-center justify-between hover:bg-gray-50 transition-colors">
          <h3 className="text-lg font-semibold text-gray-900">
            Payment Confirmation Notifications
          </h3>
          <ChevronDown
            className={cn(
              "h-5 w-5 text-gray-500 transition-transform",
              isOpen && "transform rotate-180",
            )}
          />
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className="px-6 pb-6 space-y-4 border-t border-gray-100">
            <div className="pt-4">
              <div className="flex flex-col gap-1.5 mb-2">
                <Label className="text-sm font-medium text-gray-700">
                  Additional Email Recipients
                </Label>
                <p className="text-xs text-gray-500">
                  Add recipients to receive an email when a payment is
                  submitted. Type an email address and press enter or the + to
                  confirm.
                </p>
              </div>
              <div className="flex gap-2">
                <Input
                  type="email"
                  placeholder="email@example.com"
                  value={currentEmail}
                  onChange={(e) => setCurrentEmail(e.target.value)}
                  onKeyDown={handleEmailKeyDown}
                  className="flex-1"
                  disabled={isAdmin}
                />
                {!isAdmin && (
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    onClick={handleAddEmail}
                  >
                    <Plus size={18} />
                  </Button>
                )}
              </div>

              {emailRecipients.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-2">
                  {emailRecipients.map((email) => (
                    <div
                      key={email}
                      className="flex items-center gap-2 bg-gray-100 text-gray-700 px-3 py-1.5 rounded-md text-sm"
                    >
                      <span>{email}</span>
                      {!isAdmin && (
                        <button
                          type="button"
                          onClick={() => handleRemoveEmail(email)}
                          className="text-gray-500 hover:text-red-600 transition-colors"
                        >
                          <Trash2 size={14} />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </CollapsibleContent>
      </div>
    </Collapsible>
  );
};
